// 🔍 OMNIBOX — intent-driven search: tap the search pill, get a clean category grid,
// type to find a spot by name. Picks tuck the overlay away instantly (Phase 2 remodel).
import { useState, useEffect, useRef } from "react";
import { X, Search, Flame, MapPin } from "lucide-react";

export default function Omnibox({ open, onClose, categories, activeCategory, onCategory, businesses, onPickBusiness, spotlightIds }) {
  const [q, setQ] = useState("");
  const [dealsOnly, setDealsOnly] = useState(false);
  const [maxMi, setMaxMi] = useState(null);
  const inputRef = useRef(null);
  useEffect(() => {
    if (open) { setQ(""); setDealsOnly(false); setMaxMi(null); setTimeout(() => inputRef.current?.focus(), 150); }
  }, [open]);
  if (!open) return null;
  const query = q.trim().toLowerCase();
  const dealText = (b) => {
    const off = spotlightIds?.get?.(b.id)?.offer;
    return off ? `${off.title || ""} ${off.detail || ""}` : "";
  };
  let results = query.length >= 2
    ? businesses.filter((b) => (b.name || "").toLowerCase().includes(query) || dealText(b).toLowerCase().includes(query))
    : [];
  if (dealsOnly) results = results.filter((b) => b.has_deals || spotlightIds?.get?.(b.id));
  if (maxMi) results = results.filter((b) => (b.distance_miles ?? 99) <= maxMi);
  results = results.slice(0, 14);
  const pickCat = (id) => { onCategory(id); onClose(); };
  return (
    <div className="fixed inset-0 z-[1500] bg-slate-950/95 backdrop-blur-sm animate-in fade-in duration-200" data-testid="omnibox-overlay">
      <div className="max-w-lg mx-auto px-4 pt-[max(1rem,env(safe-area-inset-top))]">
        <div className="flex items-center gap-2">
          <div className="flex-1 flex items-center gap-2 h-12 rounded-2xl bg-slate-900 border border-sky-500/40 px-3.5">
            <Search className="w-4 h-4 text-sky-400 shrink-0" />
            <input
              ref={inputRef}
              data-testid="omnibox-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search active deals or locations…"
              className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-slate-500"
            />
            {q && <button onClick={() => setQ("")} className="text-slate-500 hover:text-slate-300"><X className="w-4 h-4" /></button>}
          </div>
          <button data-testid="omnibox-close" onClick={onClose} className="w-12 h-12 rounded-2xl glass flex items-center justify-center hover:bg-slate-800/70">
            <X className="w-5 h-5 text-slate-300" />
          </button>
        </div>

        {query.length >= 2 && (
          <div className="mt-2.5 flex gap-1.5" data-testid="omnibox-filter-pills">
            <button data-testid="omnibox-pill-deals" onClick={() => setDealsOnly((v) => !v)}
              className={`rounded-full px-3 py-1.5 text-xs font-heading font-bold border transition-colors ${dealsOnly ? "bg-amber-400 text-slate-900 border-amber-400" : "bg-slate-900/80 border-slate-700 text-amber-300"}`}>
              🔥 Deals only
            </button>
            {[1, 5].map((mi) => (
              <button key={mi} data-testid={`omnibox-pill-${mi}mi`} onClick={() => setMaxMi(maxMi === mi ? null : mi)}
                className={`rounded-full px-3 py-1.5 text-xs font-heading font-bold border transition-colors ${maxMi === mi ? "bg-sky-400 text-slate-900 border-sky-400" : "bg-slate-900/80 border-slate-700 text-sky-300"}`}>
                ≤ {mi} mi
              </button>
            ))}
          </div>
        )}

        {results.length > 0 ? (
          <div className="mt-3 space-y-1.5 max-h-[66vh] overflow-y-auto pb-6" data-testid="omnibox-results">
            {results.map((b) => {
              const off = spotlightIds?.get?.(b.id)?.offer;
              return (
              <button key={b.id} data-testid={`omnibox-result-${b.id}`}
                onClick={() => { onPickBusiness(b); onClose(); }}
                className="w-full flex items-center gap-3 rounded-xl bg-slate-900/80 border border-slate-800 hover:border-sky-500/40 px-3.5 py-2.5 text-left">
                <MapPin className="w-4 h-4 text-sky-400 shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="block font-heading font-bold text-sm text-white truncate">{b.name}</span>
                  <span className="block text-[11px] text-slate-500">{b.category_label} · {b.distance_miles} mi</span>
                  {off?.title && <span className="block text-[11px] text-amber-300 truncate">🕵️ {off.title}</span>}
                </span>
                {(b.has_deals || off) && <span className="shrink-0 flex items-center gap-1 text-[10px] font-black text-amber-300 bg-amber-400/15 rounded-full px-2 py-0.5"><Flame className="w-3 h-3" /> DEAL</span>}
              </button>
              );
            })}
          </div>
        ) : query.length >= 2 ? (
          <p className="mt-6 text-center text-sm text-slate-500">No nearby spot matches "{q}" — try the categories below.</p>
        ) : null}

        {results.length === 0 && (
          <div className="mt-4 pb-6" data-testid="omnibox-categories">
            <p className="text-[10px] font-black tracking-widest text-slate-500 mb-2">BROWSE THE MAP BY CATEGORY</p>
            <div className="grid grid-cols-3 gap-1.5 max-h-[62vh] overflow-y-auto pr-1">
              <button data-testid="omnibox-cat-all" onClick={() => pickCat(null)}
                className={`h-14 rounded-xl text-xs font-heading font-bold border px-1 transition-colors ${!activeCategory ? "bg-sky-500/25 border-sky-400 text-sky-200" : "bg-slate-900/80 border-slate-800 text-slate-300 hover:border-slate-600"}`}>
                🎲 All / Random
              </button>
              {categories.map((c) => (
                <button key={c.id} data-testid={`omnibox-cat-${c.id}`} onClick={() => pickCat(c.id)}
                  className={`h-14 rounded-xl text-xs font-heading font-bold border px-1 leading-tight transition-colors ${activeCategory === c.id ? "bg-sky-500/25 border-sky-400 text-sky-200" : "bg-slate-900/80 border-slate-800 text-slate-300 hover:border-slate-600"}`}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
